import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
    useRef,
} from 'react';
import { Input } from '@rocco/components/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { Button } from '@rocco/components/button';
import { Search, LoaderCircle } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import type { StageConfiguration } from '@modules/build-hub/configs/stage-config';
import {
    getScopesByProjectType,
    getStagesForScope,
} from '@modules/build-hub/configs/stage-config';
import { useBuildHubForm } from '@modules/build-hub-creator/hooks/useBuildHubForm';
import type { BuildHubTypeEnum } from 'generated/graphql';
import { usePlaceFinder } from '~/ui-rocco/hooks/location/use-place-finder';
import { usePlaceFinderDirect } from '~/ui-rocco/hooks/location/use-place-finder-direct';
import { FindPlaceApiProviderEnum } from 'generated/graphql';
import type { SupportedCountry } from 'generated/graphql';
import { useRootData } from '~/modules/root';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';
import { formatLocation } from '@rocco/utils/format-location';
import type { LocationPlace } from '@rocco/ui/location/types/location-place';
import { FindPlaceTypeEnum } from '@archipro-website/top-navigation/generated/graphql';
import {
    BUDGET_OPTIONS,
    CUSTOM_BUDGET_OPTION,
    type BudgetOption,
} from '@modules/build-hub-creator/configs/constants';
import { useUserLoggedIn } from '@modules/user/hook/use-user';

/** Variant type for different contexts. */
type InlineCreateFormVariant = 'enquiry' | 'hde';

interface InlineCreateBuildHubFormProps {
    stageConfig: StageConfiguration;
    onBuildHubCreated: (buildHubId: string) => void;
    onBackToList: () => void;
    /**
     * Variant for the form context.
     * - 'enquiry' (default): Uses Remix useFetcher for place finder
     * - 'hde': Uses direct fetch for place finder to avoid useFetcher issues in Popovers
     */
    variant?: InlineCreateFormVariant;
}

type ProjectOwnerType = 'own-property' | 'client-project';

interface ProfessionalSelectionData {
    teamMemberID?: string;
    customCompanyName?: string;
}

/**
 * Inline form for creating a new BuildHub project within the enquiry form.
 * Follows the same flow as CreateBuildHubForm but as a single-page form.
 */
export function InlineCreateBuildHubForm({
    stageConfig,
    onBuildHubCreated,
    onBackToList,
    variant = 'enquiry',
}: InlineCreateBuildHubFormProps) {
    const { localisation } = useRootData();
    const { user, isLoggedIn } = useUserLoggedIn();
    const isArchiproStaff = isLoggedIn && user.IsArchiproStaff;
    const teamMembers = isLoggedIn ? user.TeamMembers || [] : [];

    // Determine if we should use direct fetch for place finder based on variant
    const useDirectFetch = variant === 'hde';

    // Form state
    const [projectType, setProjectType] = useState<BuildHubTypeEnum | ''>('');
    const [ownerType, setOwnerType] = useState<ProjectOwnerType | ''>('');
    const [professionalSelection, setProfessionalSelection] =
        useState<ProfessionalSelectionData>({});
    const [projectScope, setProjectScope] = useState<string>('');
    const [activeStageURLSegment, setActiveStageURLSegment] =
        useState<string>('');
    const [projectTitle, setProjectTitle] = useState<string>('');
    const [locationKeyword, setLocationKeyword] = useState<string>('');
    const [showLocationSuggestions, setShowLocationSuggestions] =
        useState(false);
    const [selectedBudget, setSelectedBudget] = useState<string>('');
    const [customBudgetAmount, setCustomBudgetAmount] = useState<string>('');

    // Validation errors
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Track buildHubId and callback refs
    const buildHubIdRef = useRef<string | null>(null);
    const hasCalledOnBuildHubCreatedRef = useRef(false);
    const onBuildHubCreatedRef = useRef(onBuildHubCreated);

    // Keep callback ref up to date
    useEffect(() => {
        onBuildHubCreatedRef.current = onBuildHubCreated;
    }, [onBuildHubCreated]);

    const {
        buildHubId,
        isCreating,
        isSubmitting,
        createError,
        updateField,
        createProject,
        submitProject,
    } = useBuildHubForm({
        onSuccess: () => {
            // Fallback if buildHubId wasn't available earlier
            if (
                buildHubIdRef.current &&
                !hasCalledOnBuildHubCreatedRef.current
            ) {
                onBuildHubCreatedRef.current(buildHubIdRef.current);
                hasCalledOnBuildHubCreatedRef.current = true;
            }
        },
        shouldNavigateOnSuccess: false,
    });

    // Derived loading state - define early so it can be used in callbacks
    const isLoading = isCreating || isSubmitting;

    // Update ref when buildHubId changes and call onBuildHubCreated immediately
    // This ensures the callback is called as soon as BuildHub is created, not waiting for full submission
    useEffect(() => {
        if (buildHubId && !hasCalledOnBuildHubCreatedRef.current) {
            buildHubIdRef.current = buildHubId;
            // Call onBuildHubCreated as soon as BuildHub is created (not waiting for full submission)
            try {
                onBuildHubCreatedRef.current(buildHubId);
                hasCalledOnBuildHubCreatedRef.current = true;
            } catch {
                // Error calling onBuildHubCreated
            }
        } else if (buildHubId) {
            buildHubIdRef.current = buildHubId;
        }
    }, [buildHubId]);

    // Location finder - both hooks called (React rules), select result based on variant
    const placeFinderFetcherResult = usePlaceFinder({
        country: localisation.country as SupportedCountry,
        type: FindPlaceTypeEnum.All,
        apiProvider: FindPlaceApiProviderEnum.Mapbox,
    });
    const placeFinderDirectResult = usePlaceFinderDirect({
        country: localisation.country as SupportedCountry,
        type: FindPlaceTypeEnum.All,
        apiProvider: FindPlaceApiProviderEnum.Mapbox,
    });
    const {
        places,
        handlePlaceFind,
        resetPlaces,
        loading: placeFinderLoading,
    } = useDirectFetch ? placeFinderDirectResult : placeFinderFetcherResult;

    // Get scope options based on project type
    const scopeOptions = useMemo(() => {
        if (!projectType) {
            return [];
        }
        return getScopesByProjectType(
            stageConfig,
            projectType as 'Residential' | 'Commercial' | 'Internal',
        );
    }, [projectType, stageConfig]);

    // Get stage options based on project type and scope
    const stageOptions = useMemo(() => {
        if (!projectType || !projectScope) {
            return [];
        }
        // Find the scope to get its label (getStagesForScope searches by label)
        const selectedScope = scopeOptions.find(
            (s) => s.value === projectScope || s.label === projectScope,
        );
        if (!selectedScope) {
            return [];
        }
        return getStagesForScope(
            stageConfig,
            projectType as 'Residential' | 'Commercial' | 'Internal',
            selectedScope.label,
        );
    }, [projectType, projectScope, stageConfig, scopeOptions]);

    // Get budget options based on project scope
    const budgetOptions = useMemo<BudgetOption[]>(() => {
        if (!projectScope) {
            return BUDGET_OPTIONS;
        }

        const scopeConfig = scopeOptions.find(
            (s) => s.value === projectScope || s.label === projectScope,
        );

        if (scopeConfig?.budgetOptions?.length) {
            // Map scope-specific budget options
            const mappedOptions = scopeConfig.budgetOptions.map((opt) => ({
                key: opt.key,
                label: opt.label,
                min: opt.min ?? undefined,
                max: opt.max ?? undefined,
            }));

            // Check if custom option already exists (could be "custom" or "budget_custom" key)
            const hasCustomOption = mappedOptions.some(
                (opt) =>
                    opt.key === 'budget_custom' ||
                    opt.key === 'custom' ||
                    opt.label.toLowerCase().includes('custom'),
            );

            // Only add custom budget option if it doesn't already exist
            if (!hasCustomOption) {
                return [...mappedOptions, CUSTOM_BUDGET_OPTION];
            }

            return mappedOptions;
        }

        return BUDGET_OPTIONS;
    }, [projectScope, scopeOptions]);

    // Handle project type change - reset dependent fields
    const handleProjectTypeChange = (value: string) => {
        setProjectType(value as BuildHubTypeEnum);
        setProjectScope('');
        setSelectedBudget('');
        updateField('projectType', value as BuildHubTypeEnum);
    };

    // Handle owner type change
    const handleOwnerTypeChange = (value: string) => {
        setOwnerType(value as ProjectOwnerType);
        setProfessionalSelection({});
        updateField('ownerType', value as ProjectOwnerType);
    };

    // Handle professional selection
    const handleProfessionalSelection = useCallback(
        (data: ProfessionalSelectionData) => {
            setProfessionalSelection(data);
            if (data.teamMemberID !== undefined) {
                updateField('ownerTeamMemberID', data.teamMemberID);
                updateField('ownerCustomCompanyName', undefined);
            } else if (data.customCompanyName !== undefined) {
                updateField('ownerCustomCompanyName', data.customCompanyName);
                updateField('ownerTeamMemberID', undefined);
            }
        },
        [updateField],
    );

    // Handle project scope change
    const handleProjectScopeChange = (value: string) => {
        setProjectScope(value);
        setSelectedBudget('');
        setActiveStageURLSegment(''); // Reset stage when scope changes

        const selectedScope = scopeOptions.find(
            (s) => s.value === value || s.label === value,
        );

        if (selectedScope) {
            // Store the display label as the project scope
            updateField('projectScope', selectedScope.label);

            // Set the project title to match the selected scope if empty
            if (!projectTitle.trim()) {
                setProjectTitle(selectedScope.label);
                updateField('title', selectedScope.label);
            }

            // Convert to StageInput format
            const stages = selectedScope.stages.map((stage) => ({
                urlSegment: stage.urlSegment,
                title: stage.title,
            }));
            updateField('stages', stages);
        }
    };

    // Handle build stage change
    const handleBuildStageChange = (value: string) => {
        setActiveStageURLSegment(value);
        updateField('activeStageURLSegment', value);
    };

    // Handle location keyword change
    const handleLocationKeywordChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        const value = event.currentTarget.value;
        setLocationKeyword(value);

        if (value.trim().length > 2) {
            handlePlaceFind(value, 10);
            setShowLocationSuggestions(true);
        } else {
            resetPlaces();
            setShowLocationSuggestions(false);
        }

        if (value.trim().length === 0) {
            setShowLocationSuggestions(false);
            // Clear location from form data
            updateField('location', undefined);
        }
    };

    // Handle location suggestion select
    const handleLocationSelect = (place: DetailedPlace) => {
        const formattedLocation =
            place.fullAddress ||
            [place.suburb, place.district || place.city, place.region]
                .filter(Boolean)
                .join(', ');
        setLocationKeyword(formattedLocation);
        setShowLocationSuggestions(false);
        resetPlaces();

        updateField('location', {
            selectedLocation: place,
            keyword: formattedLocation,
        });
    };

    // Handle budget change
    const handleBudgetChange = (value: string) => {
        setSelectedBudget(value);
        const budgetOption = budgetOptions.find((opt) => opt.key === value);

        // Check if it's a custom budget option (could be 'budget_custom' or 'custom')
        const isCustomBudget =
            value === 'budget_custom' ||
            value === 'custom' ||
            budgetOption?.label.toLowerCase().includes('custom');

        if (budgetOption && !isCustomBudget) {
            updateField('budget', {
                min: budgetOption.min,
                max: budgetOption.max,
            });
            // Clear custom budget input
            setCustomBudgetAmount('');
        } else if (isCustomBudget) {
            // Custom budget will be handled on submit
            // Don't update field yet, wait for user to enter values
        } else {
            updateField('budget', undefined);
            setCustomBudgetAmount('');
        }
    };

    // Handle custom budget input changes
    useEffect(() => {
        const isCustomBudget =
            selectedBudget === 'budget_custom' || selectedBudget === 'custom';
        if (isCustomBudget) {
            const amount = customBudgetAmount
                ? Number(customBudgetAmount)
                : undefined;
            if (amount !== undefined && !isNaN(amount)) {
                // Set both min and max to the same value (single amount)
                updateField('budget', { min: amount, max: amount });
            } else {
                updateField('budget', undefined);
            }
        }
    }, [customBudgetAmount, selectedBudget, updateField]);

    // Validate form before submission
    const validateForm = useCallback((): boolean => {
        const newErrors: Record<string, string> = {};

        if (!projectType) {
            newErrors.projectType = 'Project type is required';
        }

        if (!ownerType) {
            newErrors.ownerType = 'Owner type is required';
        }

        if (ownerType === 'client-project') {
            if (
                !professionalSelection.teamMemberID &&
                !professionalSelection.customCompanyName?.trim()
            ) {
                newErrors.professionalSelection =
                    'Please select or enter a company';
            }
        }

        if (!projectScope) {
            newErrors.projectScope = 'Project scope is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [projectType, ownerType, professionalSelection, projectScope]);

    // Track if we've initiated creation
    const hasInitiatedCreationRef = useRef(false);

    // Handle form submission
    const handleSubmit = useCallback(() => {
        if (!validateForm()) {
            return;
        }

        // Prevent double submission
        if (hasInitiatedCreationRef.current || isLoading) {
            return;
        }

        // Reset callback flag for new creation
        hasCalledOnBuildHubCreatedRef.current = false;
        buildHubIdRef.current = null;
        hasInitiatedCreationRef.current = true;

        // Ensure all form data is synced to the hook before creation
        if (projectTitle.trim()) {
            updateField('title', projectTitle.trim());
        }

        // Handle custom budget if selected
        const isCustomBudget =
            selectedBudget === 'budget_custom' || selectedBudget === 'custom';
        if (isCustomBudget) {
            const amount = customBudgetAmount
                ? Number(customBudgetAmount)
                : undefined;
            if (amount !== undefined && !isNaN(amount)) {
                // Set both min and max to the same value (single amount)
                updateField('budget', { min: amount, max: amount });
            }
        }

        // Create project with current title (or scope label as fallback)
        const titleToUse = projectTitle.trim() || projectScope || 'New Project';
        createProject(titleToUse);
    }, [
        validateForm,
        projectTitle,
        projectScope,
        createProject,
        updateField,
        selectedBudget,
        customBudgetAmount,
        isLoading,
    ]);

    // Auto-submit when BuildHub is created and we have all data
    useEffect(() => {
        if (
            buildHubId &&
            !isSubmitting &&
            !isCreating &&
            hasInitiatedCreationRef.current
        ) {
            // All data should already be in formData via updateField calls
            // Submit final update with all collected data
            submitProject();
            hasInitiatedCreationRef.current = false;
        }
    }, [buildHubId, isSubmitting, isCreating, submitProject]);

    // Format place for display
    const formatPlaceForDisplay = (place: DetailedPlace): string => {
        if (place.fullAddress) {
            return place.fullAddress;
        }

        const locationPlace: LocationPlace = {
            suburb: place.suburb,
            district: place.district,
            city: place.city,
            region: place.region,
            state: place.state,
            fullAddress: place.fullAddress,
            latitude: place.latitude,
            longitude: place.longitude,
            country: place.country,
            address1: place.address1,
            address2: place.address2,
            addressID: place.addressID,
            depth: place.depth,
            parentID: place.parentID,
            placeName: place.placeName,
            postCode: place.postCode,
            serviceableAreaID: place.serviceableAreaID,
        };

        return formatLocation(locationPlace);
    };

    const showProfessionalSelection = ownerType === 'client-project';

    return (
        <div className="flex flex-col px-3 py-2">
            <h2 className="mb-4 text-base font-medium text-gray-900">
                New Project
            </h2>

            <div className="space-y-4">
                {/* Row 1: Project Type | Owner Type */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {/* Project Type */}
                    <div>
                        <label className="mb-2 block text-3 font-medium uppercase text-[#9b9b9b]">
                            Project Type
                        </label>
                        <Select
                            value={projectType}
                            onValueChange={handleProjectTypeChange}
                        >
                            <SelectTrigger
                                color="lightOutline"
                                className="w-full"
                            >
                                <SelectValue placeholder="Please select" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Residential">
                                    Residential
                                </SelectItem>
                                <SelectItem value="Commercial">
                                    Commercial
                                </SelectItem>
                                {isArchiproStaff && (
                                    <SelectItem value="Internal">
                                        Internal
                                    </SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                        {errors.projectType && (
                            <p className="mt-1 text-sm text-red-600">
                                {errors.projectType}
                            </p>
                        )}
                    </div>

                    {/* Owner Type */}
                    <div>
                        <label className="mb-2 block text-3 font-medium uppercase text-[#9b9b9b]">
                            Who is the Project for?
                        </label>
                        <Select
                            value={ownerType}
                            onValueChange={handleOwnerTypeChange}
                        >
                            <SelectTrigger
                                color="lightOutline"
                                className="w-full"
                            >
                                <SelectValue placeholder="Please select" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="own-property">Me</SelectItem>
                                <SelectItem value="client-project">
                                    A client
                                </SelectItem>
                            </SelectContent>
                        </Select>
                        {errors.ownerType && (
                            <p className="mt-1 text-sm text-red-600">
                                {errors.ownerType}
                            </p>
                        )}
                    </div>
                </div>

                {/* Professional Selection (Conditional) - Half width with textbox */}
                {showProfessionalSelection && (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-3 font-medium uppercase text-[#9b9b9b]">
                                Which company are you representing?
                            </label>
                            <Select
                                value={
                                    professionalSelection.teamMemberID ||
                                    (professionalSelection.customCompanyName !==
                                    undefined
                                        ? 'custom'
                                        : '')
                                }
                                onValueChange={(value) => {
                                    if (value === 'custom') {
                                        handleProfessionalSelection({
                                            customCompanyName: '',
                                        });
                                    } else {
                                        handleProfessionalSelection({
                                            teamMemberID: value,
                                        });
                                    }
                                }}
                            >
                                <SelectTrigger
                                    color="lightOutline"
                                    className="w-full"
                                >
                                    <SelectValue placeholder="Please select" />
                                </SelectTrigger>
                                <SelectContent>
                                    {teamMembers.map((teamMember) => (
                                        <SelectItem
                                            key={teamMember.ID}
                                            value={String(teamMember.ID)}
                                        >
                                            <div className="flex items-center space-x-2">
                                                {teamMember.Professional
                                                    ?.CompanyLogo && (
                                                    <div
                                                        className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full"
                                                        style={{
                                                            backgroundColor:
                                                                teamMember
                                                                    .Professional
                                                                    ?.LogoBackgroundColor ||
                                                                'white',
                                                        }}
                                                    >
                                                        <img
                                                            src={
                                                                teamMember
                                                                    .Professional
                                                                    .CompanyLogo
                                                            }
                                                            alt={
                                                                teamMember
                                                                    .Professional
                                                                    ?.Title ||
                                                                ''
                                                            }
                                                            className="!h-6 !w-6 rounded-full object-contain"
                                                        />
                                                    </div>
                                                )}
                                                <span>
                                                    {teamMember.Professional
                                                        ?.Title ||
                                                        'Professional'}
                                                </span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                    <SelectItem value="custom">
                                        <div className="flex items-center space-x-2">
                                            <div className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100">
                                                <span className="text-sm font-light text-gray-600">
                                                    +
                                                </span>
                                            </div>
                                            <span>Other company</span>
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            {errors.professionalSelection && (
                                <p className="mt-1 text-sm text-red-600">
                                    {errors.professionalSelection}
                                </p>
                            )}
                        </div>
                        {professionalSelection.customCompanyName !==
                            undefined && (
                            <div>
                                <label className="mb-2 block text-3 font-medium uppercase text-[#9b9b9b]">
                                    Company name
                                </label>
                                <Input
                                    type="text"
                                    text="default"
                                    placeholder="Enter company name"
                                    color="lightOutline"
                                    value={
                                        professionalSelection.customCompanyName ||
                                        ''
                                    }
                                    onChange={(e) =>
                                        handleProfessionalSelection({
                                            customCompanyName: e.target.value,
                                        })
                                    }
                                    className="h-12"
                                    autoFocus
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Row 2: Project Scope | Build Stage */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {/* Project Scope */}
                    <div>
                        <label className="mb-2 block text-3 font-medium uppercase text-[#9b9b9b]">
                            Project Scope
                        </label>
                        <Select
                            value={projectScope}
                            onValueChange={handleProjectScopeChange}
                            disabled={!projectType}
                        >
                            <SelectTrigger
                                color="lightOutline"
                                className="w-full"
                            >
                                <SelectValue placeholder="Please select" />
                            </SelectTrigger>
                            <SelectContent>
                                {scopeOptions.map((scope) => (
                                    <SelectItem
                                        key={scope.value}
                                        value={scope.value}
                                    >
                                        {scope.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {errors.projectScope && (
                            <p className="mt-1 text-sm text-red-600">
                                {errors.projectScope}
                            </p>
                        )}
                    </div>

                    {/* Build Stage */}
                    <div>
                        <label className="mb-2 block text-3 font-medium uppercase text-[#9b9b9b]">
                            Build Stage
                        </label>
                        <Select
                            value={activeStageURLSegment}
                            onValueChange={handleBuildStageChange}
                            disabled={!projectScope}
                        >
                            <SelectTrigger
                                color="lightOutline"
                                className="w-full"
                            >
                                <SelectValue placeholder="Please select" />
                            </SelectTrigger>
                            <SelectContent>
                                {stageOptions.map((stage) => (
                                    <SelectItem
                                        key={stage.urlSegment}
                                        value={stage.urlSegment}
                                    >
                                        {stage.title}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Row 3: Project Location | Project Budget - Hidden for Internal projects */}
                {projectType !== 'Internal' && (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {/* Project Location */}
                        <div className="relative">
                            <label className="mb-2 block text-3 font-medium uppercase text-[#9b9b9b]">
                                Project Location
                            </label>
                            <Input
                                type="text"
                                placeholder="Enter a location"
                                color="lightOutline"
                                value={locationKeyword}
                                onChange={handleLocationKeywordChange}
                                className="w-full"
                                text="default"
                                postfix={
                                    placeFinderLoading ? (
                                        <LoaderCircle className="size-4 animate-spin" />
                                    ) : (
                                        <Search
                                            className={cn(
                                                'size-4',
                                                locationKeyword.length === 0 &&
                                                    'opacity-50',
                                            )}
                                        />
                                    )
                                }
                            />
                            {showLocationSuggestions && places.length > 0 && (
                                <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                                    {places.map((place, index) => (
                                        <div
                                            key={index}
                                            className="cursor-pointer border-b border-gray-100 px-4 py-3 last:border-b-0 hover:bg-gray-50"
                                            onClick={() =>
                                                handleLocationSelect(place)
                                            }
                                        >
                                            <div className="text-sm text-gray-900">
                                                {formatPlaceForDisplay(place)}
                                            </div>
                                            {place.postCode && (
                                                <div className="mt-1 text-xs text-gray-500">
                                                    {place.postCode}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Project Budget */}
                        <div>
                            <label className="mb-3 block text-3 font-medium uppercase text-[#9b9b9b]">
                                Project Budget
                            </label>
                            <Select
                                value={selectedBudget}
                                onValueChange={handleBudgetChange}
                                disabled={!projectScope}
                            >
                                <SelectTrigger
                                    color="lightOutline"
                                    className="w-full"
                                >
                                    <SelectValue placeholder="Please select" />
                                </SelectTrigger>
                                <SelectContent>
                                    {budgetOptions.map((option) => (
                                        <SelectItem
                                            key={option.key}
                                            value={option.key}
                                        >
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {(selectedBudget === 'budget_custom' ||
                                selectedBudget === 'custom') && (
                                <div className="mt-2">
                                    <Input
                                        type="number"
                                        placeholder="e.g. 7,500,000"
                                        value={customBudgetAmount}
                                        onChange={(e) =>
                                            setCustomBudgetAmount(
                                                e.target.value,
                                            )
                                        }
                                        className="h-10"
                                        text="default"
                                        color="lightOutline"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Error message */}
            {(createError || Object.keys(errors).length > 0) && (
                <div className="mt-4 rounded-md bg-red-50 p-3">
                    <p className="text-sm text-red-800">
                        {createError ||
                            'Please fix the errors above before submitting'}
                    </p>
                </div>
            )}

            {/* Footer buttons */}
            <div className="mt-6 flex items-center justify-between">
                <Button
                    type="button"
                    color="secondary"
                    onClick={onBackToList}
                    disabled={isLoading}
                >
                    Back
                </Button>
                <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    isLoading={isLoading}
                >
                    {isLoading ? 'Creating...' : 'Save Project'}
                </Button>
            </div>
        </div>
    );
}
